/**
 * Iterate a DRF page-number-paginated endpoint until `next` is null and
 * return the concatenated `results`.
 *
 * `fetchPage` receives a URLSearchParams containing `page` and `page_size`
 * and must resolve to `{ count, next, previous, results }`. A bare array
 * response is returned as-is so callers keep working against a server that
 * has not been paginated yet.
 */
const DEFAULT_PAGE_SIZE = 200;
const MAX_PAGES = 1000;

const fetchAllPages = async (
  fetchPage,
  { pageSize = DEFAULT_PAGE_SIZE, params } = {},
) => {
  const items = [];

  for (let page = 1; page <= MAX_PAGES; page++) {
    const query = new URLSearchParams(params);
    query.set('page', String(page));
    query.set('page_size', String(pageSize));

    const response = await fetchPage(query);

    if (Array.isArray(response)) {
      return page === 1 ? response : items.concat(response);
    }

    const results = Array.isArray(response?.results) ? response.results : [];
    items.push(...results);

    if (!response?.next || results.length === 0) {
      return items;
    }
  }

  // Never hand back a silently truncated list; that is the failure mode this
  // helper exists to prevent.
  throw new Error(
    `fetchAllPages: exceeded ${MAX_PAGES} pages while "next" was still present`,
  );
};

export default fetchAllPages;
